import React, { createContext, useContext, useState, useEffect } from 'react';
import { User } from '../../shared/types.ts';

interface AuthContextType {
  user: User | null;
  token: string | null;
  isLoading: boolean;
  login: (email: string, password?: string) => Promise<{
    success: boolean;
    requiresVerification?: boolean;
    email?: string;
    error?: string;
  }>;
  verifyEmail: (email: string, code: string) => Promise<{ success: boolean; error?: string }>;
  resendVerificationCode: (email: string) => Promise<{ success: boolean; code?: string; error?: string }>;
  logout: () => Promise<void>;
  updateUser: (user: User) => void;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  token: null,
  isLoading: false,
  login: async () => ({ success: true }),
  verifyEmail: async () => ({ success: true }),
  resendVerificationCode: async () => ({ success: true }),
  logout: async () => {},
  updateUser: () => {},
});

// A new sign-in always opens the Workspace (the dashboard) instead of whatever screen the address bar
// still shows from last time. A shared link to one task is the exception, so that link still works
// for someone who had to sign in first. A page reload while signed in does not come through here,
// so it keeps the current screen.
const openWorkspaceAfterSignIn = () => {
  try {
    if (!/^\/tasks\/[^/]+/.test(window.location.pathname)) {
      window.history.replaceState({}, '', '/');
    }
  } catch {
    // no history access (very old or sandboxed browser): the app just keeps its current address
  }
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(() => localStorage.getItem('nexus_auth_token'));
  const [isLoading, setIsLoading] = useState<boolean>(() => {
    // Only show initial loading screen if there is a saved token to verify
    return !!localStorage.getItem('nexus_auth_token');
  });

  // Verify stored session token on application boot
  useEffect(() => {
    const checkSession = async () => {
      const savedToken = localStorage.getItem('nexus_auth_token');
      if (!savedToken) {
        setUser(null);
        setToken(null);
        setIsLoading(false);
        return;
      }

      try {
        const res = await fetch('/api/auth/me', {
          headers: {
            Authorization: `Bearer ${savedToken}`,
          },
        });

        if (res.ok) {
          const data = await res.json();
          if (data.user) {
            setUser(data.user);
            setToken(savedToken);
          } else {
            localStorage.removeItem('nexus_auth_token');
            setUser(null);
            setToken(null);
          }
        } else {
          // Token expired or invalid
          localStorage.removeItem('nexus_auth_token');
          setUser(null);
          setToken(null);
        }
      } catch (err) {
        console.error('Failed to verify session token:', err);
        localStorage.removeItem('nexus_auth_token');
        setUser(null);
        setToken(null);
      } finally {
        setIsLoading(false);
      }
    };

    checkSession();
  }, []);

  const login = async (email: string, password?: string) => {
    try {
      const res = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim(), password }),
      });
      const data = await res.json();
      if (!res.ok) {
        if (data.requiresVerification) {
          return {
            success: false,
            requiresVerification: true,
            email: data.email || email.trim(),
            error: data.error,
          };
        }
        return { success: false, error: data.error || 'Authentication failed.' };
      }

      if (data.token) {
        setToken(data.token);
        localStorage.setItem('nexus_auth_token', data.token);
      }
      if (data.user) {
        openWorkspaceAfterSignIn();
        setUser(data.user);
      }
      return { success: true };
    } catch (err: any) {
      return { success: false, error: err.message || 'Unable to connect to authentication service.' };
    }
  };

  const verifyEmail = async (email: string, code: string) => {
    try {
      const res = await fetch('/api/auth/verify-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim(), code: code.trim() }),
      });
      const data = await res.json();
      if (!res.ok) {
        return { success: false, error: data.error || 'Verification failed.' };
      }
      if (data.token) {
        setToken(data.token);
        localStorage.setItem('nexus_auth_token', data.token);
      }
      if (data.user) {
        openWorkspaceAfterSignIn();
        setUser(data.user);
      }
      return { success: true };
    } catch (err: any) {
      return { success: false, error: err.message || 'Network error during email verification.' };
    }
  };

  const resendVerificationCode = async (email: string) => {
    try {
      const res = await fetch('/api/auth/resend-code', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim() }),
      });
      const data = await res.json();
      if (!res.ok) {
        return { success: false, error: data.error || 'Failed to resend code.' };
      }
      return { success: true, code: data.code };
    } catch (err: any) {
      return { success: false, error: err.message || 'Network error.' };
    }
  };

  const logout = async () => {
    try {
      await fetch('/api/auth/logout', { method: 'POST' });
    } catch (e) {}
    localStorage.removeItem('nexus_auth_token');
    localStorage.removeItem('nexus_demo_role');
    localStorage.removeItem('nexus_token_admin');
    localStorage.removeItem('nexus_token_member');
    // The next sign-in starts from the Workspace, not from the screen that was open
    openWorkspaceAfterSignIn();
    setUser(null);
    setToken(null);
  };

  const updateUser = (updated: User) => {
    setUser(updated);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        isLoading,
        login,
        verifyEmail,
        resendVerificationCode,
        logout,
        updateUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
